import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Building2, Users, Settings, LogOut, Plus, User, Palette, 
  QrCode, ExternalLink, Upload, Mail, Trash2, Clock, Download, FileUp, Share2,
  Edit, Ban, Power, Check
} from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";

const CompanyAdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState("branding");
  const [company, setCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [landingSettings, setLandingSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companyRes, usersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/companies/${user.company_id}`, authHeader),
        axios.get(`${API}/companies/${user.company_id}/users`, authHeader),
        axios.get(`${API}/landing-settings`)
      ]);
      setCompany(companyRes.data);
      setCompanyUsers(usersRes.data);
      setLandingSettings(settingsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: company?.primary_color || "#4F46E5" }}
            >
              {company?.name?.charAt(0) || "C"}
            </div>
            <div>
              <h2 className="font-semibold">{company?.name}</h2>
              <p className="text-xs text-muted-foreground">Company Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="my-profile-btn"
            >
              <User className="h-4 w-4 mr-2" /> My Profile
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button 
            variant={activeTab === "branding" ? "default" : "outline"}
            onClick={() => setActiveTab("branding")}
            data-testid="tab-branding"
          >
            <Palette className="h-4 w-4 mr-2" /> Branding
          </Button>
          <Button 
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            data-testid="tab-users"
          >
            <Users className="h-4 w-4 mr-2" /> Team Members
          </Button>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 pb-24">
        {activeTab === "branding" && (
          <BrandingTab 
            company={company} 
            token={token} 
            onRefresh={fetchData}
          />
        )}
        {activeTab === "users" && (
          <TeamMembersTab 
            company={company}
            companyId={user.company_id}
            users={companyUsers}
            token={token}
            onRefresh={fetchData}
          />
        )}
      </main>

      <WhatsAppButton phone={landingSettings?.support_whatsapp} />
    </div>
  );
};

// Branding Tab
const BrandingTab = ({ company, token, onRefresh }) => {
  const [form, setForm] = useState({
    name: company?.name || "",
    primary_color: company?.primary_color || "#4F46E5",
    secondary_color: company?.secondary_color || "#10B981",
    theme: company?.theme || "light",
    logo_url: company?.logo_url || "",
    wallet_logo_url: company?.wallet_logo_url || "",
    welcome_title: company?.welcome_title || "",
    welcome_subtitle: company?.welcome_subtitle || ""
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingWallet, setUploadingWallet] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/companies/${company.id}`, form, authHeader);
      toast.success("Company branding updated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update branding");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setForm({ ...form, logo_url: response.data.logo_url });
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleWalletLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingWallet(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setForm({ ...form, wallet_logo_url: response.data.logo_url });
      toast.success("Wallet logo uploaded");
    } catch (error) {
      toast.error("Failed to upload wallet logo");
    } finally {
      setUploadingWallet(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <h1 className="text-2xl font-bold">Company Branding</h1>
      <p className="text-muted-foreground">
        These settings will apply to all team members&apos; profiles.
      </p>

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input
              data-testid="company-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Company Logo</Label>
            <div className="flex items-center gap-4">
              {form.logo_url && (
                <img 
                  src={form.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url} 
                  alt="Logo" 
                  className="h-16 w-auto rounded-lg"
                />
              )}
              <label className="cursor-pointer">
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <Button variant="outline" asChild disabled={uploading}>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Upload Logo"}
                  </span>
                </Button>
              </label>
            </div>
          </div>

          <div className="space-y-2">
            <Label>White Logo for Apple Wallet</Label>
            <p className="text-xs text-muted-foreground">
              Upload a WHITE version of your logo (transparent PNG) — used on the Apple Wallet card for your team members.
            </p>
            <div className="flex items-center gap-4">
              {form.wallet_logo_url && (
                <div className="p-2 rounded-lg" style={{ backgroundColor: form.primary_color }}>
                  <img
                    src={form.wallet_logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.wallet_logo_url}` : form.wallet_logo_url}
                    alt="Wallet Logo"
                    className="h-10 w-auto"
                  />
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/png"
                  className="hidden"
                  onChange={handleWalletLogoUpload}
                  data-testid="wallet-logo-input"
                />
                <Button variant="outline" asChild disabled={uploadingWallet}>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingWallet ? "Uploading..." : "Upload White Logo"}
                  </span>
                </Button>
              </label>
              {form.wallet_logo_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setForm({ ...form, wallet_logo_url: "" })}
                  data-testid="wallet-logo-remove"
                >
                  Remove
                </Button>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Primary Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={form.secondary_color}
                  onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={form.secondary_color}
                  onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Theme</Label>
            <Select value={form.theme} onValueChange={(v) => setForm({ ...form, theme: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light Theme</SelectItem>
                <SelectItem value="dark">Dark Theme</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-4 space-y-4">
            <div>
              <Label className="text-base font-medium">Custom Welcome Message</Label>
              <p className="text-xs text-muted-foreground">Shown on your company registration page (/join/{company?.slug}).</p>
            </div>
            <div className="space-y-2">
              <Label>Welcome Title</Label>
              <Input
                data-testid="welcome-title-input"
                value={form.welcome_title}
                onChange={(e) => setForm({ ...form, welcome_title: e.target.value })}
                placeholder={`Join ${form.name || "our company"}`}
              />
            </div>
            <div className="space-y-2">
              <Label>Welcome Subtitle</Label>
              <Textarea
                data-testid="welcome-subtitle-input"
                value={form.welcome_subtitle}
                onChange={(e) => setForm({ ...form, welcome_subtitle: e.target.value })}
                placeholder="Create your digital business card"
                rows={2}
              />
            </div>
          </div>

          <Button onClick={handleSave} disabled={loading} data-testid="save-branding-btn">
            {loading ? "Saving..." : "Save Branding"}
          </Button>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="p-6 rounded-xl text-center"
            style={{ 
              backgroundColor: form.theme === "dark" ? "#18181B" : "#FAFAFA",
              color: form.theme === "dark" ? "#FAFAFA" : "#09090B"
            }}
          >
            {form.logo_url ? (
              <img 
                src={form.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url}
                alt="Logo" 
                className="h-12 mx-auto mb-4"
              />
            ) : (
              <div 
                className="h-12 w-12 rounded-xl mx-auto mb-4 flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: form.primary_color }}
              >
                {form.name?.charAt(0) || "C"}
              </div>
            )}
            <h3 className="font-semibold">{form.name}</h3>
            <div className="flex justify-center gap-2 mt-4">
              <span 
                className="px-4 py-2 rounded-full text-white text-sm"
                style={{ backgroundColor: form.primary_color }}
              >
                Primary Button
              </span>
              <span 
                className="px-4 py-2 rounded-full text-white text-sm"
                style={{ backgroundColor: form.secondary_color }}
              >
                Secondary
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Team Members Tab
const TeamMembersTab = ({ company, companyId, users, token, onRefresh }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", is_company_admin: false });
  const [loading, setLoading] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", is_company_admin: false });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkData, setBulkData] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", profession: "", profession_details: "", email: "", email_public: "",
    phone: "", whatsapp: "", bio: "", location: "", website: "",
    linkedin: "", instagram: "", facebook: "", twitter: "", tiktok: "", snapchat: "", youtube: "",
    photo_url: "", status: "approved", password: ""
  });
  const [uploadingMemberPhoto, setUploadingMemberPhoto] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchInvitations = async () => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}/invitations`, authHeader);
      setInvitations(res.data);
    } catch (error) {
      // non-blocking
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/companies/${companyId}/users`, form, authHeader);
      toast.success("Team member added");
      setShowDialog(false);
      setForm({ name: "", email: "", password: "", is_company_admin: false });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast.error("Please enter an email address");
      return;
    }
    setInviteLoading(true);
    try {
      await axios.post(`${API}/companies/${companyId}/invitations`, {
        ...inviteForm,
        base_url: window.location.origin,
      }, authHeader);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ name: "", email: "", is_company_admin: false });
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      await axios.delete(`${API}/companies/${companyId}/invitations/${inviteId}`, authHeader);
      toast.success("Invitation revoked");
      fetchInvitations();
    } catch (error) {
      toast.error("Failed to revoke invitation");
    }
  };

  const copyRegistrationLink = () => {
    const url = `${window.location.origin}/join/${company?.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Registration link copied!");
  };

  const exportUsers = () => {
    if (!users.length) {
      toast.error("No users to export");
      return;
    }
    const headers = [
      "Name", "Role", "Status", "Login Email", "Public Email", "Profession", "Profession Details",
      "Phone", "WhatsApp", "Location", "Website", "Bio",
      "LinkedIn", "Instagram", "Facebook", "X (Twitter)", "TikTok", "Snapchat", "YouTube",
      "Profile Link"
    ];
    const rows = users.map(u => [
      u.name || "", u.role || "", u.status || "", u.email || "", u.email_public || "",
      u.profession || "", u.profession_details || "", u.phone || "", u.whatsapp || "",
      u.location || "", u.website || "", u.bio || "",
      u.linkedin || "", u.instagram || "", u.facebook || "", u.twitter || "",
      u.tiktok || "", u.snapchat || "", u.youtube || "",
      `${window.location.origin}/profile/${u.slug}`,
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(company?.name || "company").replace(/[^a-z0-9]/gi, "_")}_users.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${users.length} users`);
  };

  const downloadTemplate = () => {
    const template = `name,email,password,profession,phone,whatsapp,linkedin,location,website,bio
John Doe,john@example.com,Welcome123!,Software Engineer,+1234567890,+1234567890,https://linkedin.com/in/johndoe,New York,https://johndoe.com,Experienced developer`;
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_users_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setBulkData(ev.target.result);
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!bulkData.trim()) {
      toast.error("Please enter user data");
      return;
    }
    setLoading(true);
    setBulkResult(null);
    try {
      const lines = bulkData.trim().split("\n");
      const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
      const parsed = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map(v => v.trim());
        if (values.length < 2) continue;
        const u = {};
        headers.forEach((h, idx) => { if (values[idx]) u[h] = values[idx]; });
        if (u.name && u.email) parsed.push(u);
      }
      if (parsed.length === 0) {
        toast.error("No valid users found in the data");
        setLoading(false);
        return;
      }
      const res = await axios.post(`${API}/companies/${companyId}/users/bulk`, { users: parsed, company_id: companyId }, authHeader);
      setBulkResult(res.data);
      if (res.data.created > 0) {
        toast.success(`Created ${res.data.created} users`);
        onRefresh();
      }
      if (res.data.errors > 0) toast.warning(`${res.data.errors} rows had errors`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import users");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    if (action === "delete" && !window.confirm(`Permanently delete ${selectedIds.length} selected member(s)?`)) return;
    try {
      const res = await axios.post(`${API}/companies/${companyId}/users/bulk-action`, { user_ids: selectedIds, action }, authHeader);
      toast.success(`${action} applied to ${res.data.affected} member(s)`);
      setSelectedIds([]);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk action failed");
    }
  };

  const openEdit = (member) => {
    setEditingMember(member);
    setEditForm({
      name: member.name || "",
      profession: member.profession || "",
      profession_details: member.profession_details || "",
      email: member.email || "",
      email_public: member.email_public || "",
      phone: member.phone || "",
      whatsapp: member.whatsapp || "",
      bio: member.bio || "",
      location: member.location || "",
      website: member.website || "",
      linkedin: member.linkedin || "",
      instagram: member.instagram || "",
      facebook: member.facebook || "",
      twitter: member.twitter || "",
      tiktok: member.tiktok || "",
      snapchat: member.snapchat || "",
      youtube: member.youtube || "",
      photo_url: member.photo_url || "",
      status: member.status || "approved",
      password: "",
    });
    setShowEditDialog(true);
  };

  const handleMemberPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMemberPhoto(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setEditForm((f) => ({ ...f, photo_url: res.data.logo_url }));
      toast.success("Photo uploaded");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploadingMemberPhoto(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.email) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.put(`${API}/companies/${companyId}/users/${editingMember.id}`, payload, authHeader);
      toast.success("Member updated");
      setShowEditDialog(false);
      setEditingMember(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberStatus = async (member, status) => {
    try {
      await axios.put(`${API}/companies/${companyId}/users/${member.id}`, { status }, authHeader);
      toast.success(status === "disapproved" ? "Member suspended" : "Member reactivated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update member");
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Permanently delete ${member.name}'s account?`)) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/users/${member.id}`, authHeader);
      toast.success("Member deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete member");
    }
  };

  const [memberSearch, setMemberSearch] = useState("");
  const [memberStatus, setMemberStatus] = useState("all");
  const [memberRole, setMemberRole] = useState("all");
  const [memberSort, setMemberSort] = useState("name");

  const filteredMembers = users
    .filter((u) => {
      if (memberStatus !== "all" && (u.status || "approved") !== memberStatus) return false;
      if (memberRole !== "all" && u.role !== memberRole) return false;
      if (memberSearch) {
        const q = memberSearch.toLowerCase();
        return (
          u.name?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.profession?.toLowerCase().includes(q)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (memberSort === "name") return (a.name || "").localeCompare(b.name || "");
      if (memberSort === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
      if (memberSort === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
      return 0;
    });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Team Members</h1>
          <p className="text-muted-foreground">{filteredMembers.length} of {users.length} members</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={copyRegistrationLink} data-testid="copy-reg-link-btn">
            <Share2 className="h-4 w-4 mr-2" /> Copy Link
          </Button>
          <Button variant="outline" onClick={exportUsers} data-testid="export-users-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="bulk-import-btn">
                <FileUp className="h-4 w-4 mr-2" /> Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Bulk Import Team Members</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex gap-2 flex-wrap">
                  <Button variant="outline" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-2" /> Download Template
                  </Button>
                  <label>
                    <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
                    <Button variant="outline" asChild>
                      <span><FileUp className="h-4 w-4 mr-2" /> Upload CSV</span>
                    </Button>
                  </label>
                </div>
                <div className="space-y-2">
                  <Label>CSV Data (paste or upload)</Label>
                  <Textarea
                    data-testid="bulk-data-input"
                    value={bulkData}
                    onChange={(e) => setBulkData(e.target.value)}
                    placeholder={"name,email,password,profession,phone\nJohn Doe,john@example.com,Welcome123!,Engineer,+1234567890"}
                    rows={9}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Required: name, email. Optional: password (auto-generated if empty), profession, phone, whatsapp, linkedin, location, website, bio.
                  </p>
                </div>
                {bulkResult && (
                  <div className="flex gap-4">
                    <div className="flex-1 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm text-green-600">Created: {bulkResult.created}</div>
                    <div className="flex-1 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600">Errors: {bulkResult.errors}</div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Close</Button>
                <Button onClick={handleBulkImport} disabled={loading || !bulkData.trim()} data-testid="submit-bulk-btn">
                  {loading ? "Importing..." : "Import Users"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="invite-member-btn">
                <Mail className="h-4 w-4 mr-2" /> Invite by Email
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    data-testid="invite-email-input"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="member@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Name (optional)</Label>
                  <Input
                    data-testid="invite-member-name-input"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Make Company Admin</Label>
                  <Switch
                    checked={inviteForm.is_company_admin}
                    onCheckedChange={(v) => setInviteForm({ ...inviteForm, is_company_admin: v })}
                    data-testid="invite-admin-switch"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  We&apos;ll email an invitation link. They set their own name, photo and password.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                <Button onClick={handleInvite} disabled={inviteLoading} data-testid="submit-invite-btn">
                  {inviteLoading ? "Sending..." : "Send Invitation"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    data-testid="member-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    data-testid="member-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="john@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    data-testid="member-password-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Initial password"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Make Company Admin</Label>
                  <Switch
                    checked={form.is_company_admin}
                    onCheckedChange={(v) => setForm({ ...form, is_company_admin: v })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={loading} data-testid="submit-member-btn">
                  {loading ? "Adding..." : "Add Member"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {invitations.length > 0 && (
        <div className="space-y-3" data-testid="pending-invitations">
          <h2 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" /> Pending Invitations ({invitations.length})
          </h2>
          <div className="grid gap-2">
            {invitations.map((inv) => (
              <Card key={inv.id} className="border-dashed" data-testid={`invitation-card-${inv.id}`}>
                <CardContent className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{inv.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Invited{inv.is_company_admin ? " as Admin" : ""} · awaiting acceptance
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevoke(inv.id)}
                    data-testid={`revoke-invite-${inv.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid sm:grid-cols-4 gap-3 p-4 rounded-lg border">
        <div className="space-y-1 sm:col-span-2">
          <Label className="text-xs">Search</Label>
          <Input
            data-testid="member-search-input"
            placeholder="Name, email, profession..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={memberStatus} onValueChange={setMemberStatus}>
            <SelectTrigger data-testid="member-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="disapproved">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Sort by</Label>
          <Select value={memberSort} onValueChange={setMemberSort}>
            <SelectTrigger data-testid="member-sort-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A-Z)</SelectItem>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Role</Label>
          <Select value={memberRole} onValueChange={setMemberRole}>
            <SelectTrigger data-testid="member-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="company_admin">Admins</SelectItem>
              <SelectItem value="company_user">Members</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk action bar */}
      {filteredMembers.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3 p-3 rounded-lg border bg-muted/40">
          <label className="flex items-center gap-2 text-sm cursor-pointer font-medium">
            <input
              type="checkbox"
              className="h-4 w-4"
              data-testid="select-all-members"
              checked={selectedIds.length > 0 && filteredMembers.every((u) => selectedIds.includes(u.id))}
              onChange={(e) => setSelectedIds(e.target.checked ? filteredMembers.map((u) => u.id) : [])}
            />
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
          </label>
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("approve")} data-testid="bulk-approve"><Check className="h-4 w-4 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("reactivate")} data-testid="bulk-reactivate"><Power className="h-4 w-4 mr-1" />Reactivate</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("hold")} data-testid="bulk-hold"><Clock className="h-4 w-4 mr-1" />On Hold</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("suspend")} data-testid="bulk-suspend"><Ban className="h-4 w-4 mr-1" />Suspend</Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleBulkAction("delete")} data-testid="bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3">
        {filteredMembers.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-muted-foreground">No members match your filters.</CardContent></Card>
        ) : filteredMembers.map(u => (
          <Card key={u.id} data-testid={`member-card-${u.id}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    data-testid={`select-member-${u.id}`}
                    checked={selectedIds.includes(u.id)}
                    onChange={() => toggleSelect(u.id)}
                  />
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-medium">
                    {u.name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                    {u.profession && <p className="text-xs text-muted-foreground">{u.profession}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={u.role === "company_admin" ? "default" : "outline"}>
                    {u.role === "company_admin" ? "Admin" : "Member"}
                  </Badge>
                  {u.status && u.status !== "approved" && (
                    <Badge variant="outline" className="border-destructive text-destructive">
                      {u.status === "disapproved" ? "Suspended" : "On Hold"}
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-member-${u.id}`}>
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(`/profile/${u.slug}`, "_blank")} data-testid={`ecard-member-${u.id}`}>
                    <Share2 className="h-4 w-4 mr-1" /> E-Card
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleMemberStatus(u, u.status === "disapproved" ? "approved" : "disapproved")}
                    data-testid={`suspend-member-${u.id}`}
                  >
                    {u.status === "disapproved" ? (<><Power className="h-4 w-4 mr-1" /> Reactivate</>) : (<><Ban className="h-4 w-4 mr-1" /> Suspend</>)}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteMember(u)} data-testid={`delete-member-${u.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Photo */}
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-muted overflow-hidden flex items-center justify-center font-medium">
                {editForm.photo_url ? (
                  <img
                    src={editForm.photo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${editForm.photo_url}` : editForm.photo_url}
                    alt="Member"
                    className="h-full w-full object-cover"
                  />
                ) : (editForm.name?.charAt(0) || "?")}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleMemberPhotoUpload} data-testid="edit-member-photo-input" />
                <Button variant="outline" size="sm" asChild disabled={uploadingMemberPhoto}>
                  <span><Upload className="h-4 w-4 mr-2" />{uploadingMemberPhoto ? "Uploading..." : "Change Photo"}</span>
                </Button>
              </label>
            </div>

            {/* Basic */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input data-testid="edit-member-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Profession</Label>
                <Input data-testid="edit-member-profession" value={editForm.profession} onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })} placeholder="e.g., Cardiologist" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Profession Details</Label>
              <Textarea data-testid="edit-member-profession-details" rows={2} value={editForm.profession_details} onChange={(e) => setEditForm({ ...editForm, profession_details: e.target.value })} placeholder="Specialization, department..." />
            </div>
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea data-testid="edit-member-bio" rows={2} value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input data-testid="edit-member-location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} placeholder="Dubai, UAE" />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input data-testid="edit-member-website" value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://..." />
              </div>
            </div>

            {/* Contact */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium">Contact</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Login Email</Label>
                  <Input data-testid="edit-member-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Public Email</Label>
                  <Input data-testid="edit-member-email-public" type="email" value={editForm.email_public} onChange={(e) => setEditForm({ ...editForm, email_public: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input data-testid="edit-member-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="+1 234 567 8900" />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp</Label>
                  <Input data-testid="edit-member-whatsapp" value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} placeholder="+1 234 567 8900" />
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium">Social Links</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["linkedin", "LinkedIn"], ["instagram", "Instagram"], ["facebook", "Facebook"],
                  ["twitter", "X (Twitter)"], ["tiktok", "TikTok"], ["snapchat", "Snapchat"], ["youtube", "YouTube"],
                ].map(([key, label]) => (
                  <div className="space-y-2" key={key}>
                    <Label>{label}</Label>
                    <Input
                      data-testid={`edit-member-${key}`}
                      value={editForm[key]}
                      onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="border-t pt-4 space-y-2">
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger data-testid="edit-member-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="disapproved">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reset password */}
            <div className="border-t pt-4 space-y-2">
              <Label>Reset Password (optional)</Label>
              <Input
                data-testid="edit-member-password"
                type="text"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Leave blank to keep current"
              />
              <p className="text-xs text-muted-foreground">Set a new login password for this member (min 6 characters), then share it with them.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={loading} data-testid="save-member-btn">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default CompanyAdminDashboard;
